import type { Attachment, Note, Notebook, Tag, Task } from "@engram/core";
import { newId, parseTagIds } from "@engram/core/id";
import { toggleCompletion } from "@engram/core/tasks";
import { db } from "./client";
import type { LocalMutation } from "./protocol";
import { rememberNewNote } from "../ui/emptyNotes";

/* All writes go through here, so every change lands in the replica and the outbox together. */

export async function getSetting<T>(key: string): Promise<T | undefined> {
  const row = await db.get<{ value: string }>("SELECT value FROM settings WHERE id = ? AND deleted = 0", [key]);
  return row ? (JSON.parse(row.value) as T) : undefined;
}

export async function setSetting(key: string, value: unknown) {
  await db.mutate([{ t: "settings", id: key, set: { value: JSON.stringify(value), updated: Date.now() } }]);
}

export async function defaultNotebookId(): Promise<string> {
  const id = await getSetting<string>("defaultNotebook");
  if (id && (await db.get("SELECT 1 FROM notebooks WHERE id = ? AND deleted = 0", [id]))) return id;
  const first = await db.get<{ id: string }>("SELECT id FROM notebooks WHERE deleted = 0 AND kind = 'notebook' ORDER BY created LIMIT 1");
  if (first) {
    await setSetting("defaultNotebook", first.id);
    return first.id;
  }
  const nb = await createNotebook("First Notebook");
  await setSetting("defaultNotebook", nb);
  return nb;
}

export async function createNote(opts: { notebookId?: string; title?: string; bodyHtml?: string; bodyText?: string; tagIds?: string[] } = {}): Promise<string> {
  const id = newId();
  const now = Date.now();
  const wanted = opts.notebookId && (await db.get("SELECT 1 FROM notebooks WHERE id = ? AND deleted = 0 AND kind = 'notebook'", [opts.notebookId])) ? opts.notebookId : undefined;
  const notebook_id = wanted ?? (await defaultNotebookId());
  await db.mutate([
    {
      t: "notes",
      id,
      set: {
        notebook_id,
        title: opts.title ?? "",
        body_html: opts.bodyHtml ?? "",
        body_text: opts.bodyText ?? "",
        tag_ids: JSON.stringify(opts.tagIds ?? []),
        created: now,
        updated: now,
        trashed_at: null,
        source_url: null,
        pinned_at: null,
      },
    },
  ]);
  // Left empty, it goes away by itself (ui/emptyNotes.ts).
  rememberNewNote(id, now);
  return id;
}

/** `base`: for title/body edits from the editor, the version the edited text started from (see LocalMutation). */
export function updateNote(id: string, set: Partial<Pick<Note, "title" | "body_html" | "body_text" | "notebook_id" | "tag_ids" | "pinned_at" | "source_url">>, base?: number) {
  return db.mutate([{ t: "notes", id, set: { ...set, updated: Date.now() }, ...(base !== undefined && { base }) }]);
}

/** Evernote's edit protection: a locked note opens read-only until it's unlocked. Doesn't change "updated". */
export function setNoteLocked(id: string, locked: boolean) {
  return db.mutate([{ t: "notes", id, set: { locked_at: locked ? Date.now() : null } }]);
}

export function trashNotes(ids: string[]) {
  const now = Date.now();
  return db.mutate(ids.map((id) => ({ t: "notes" as const, id, set: { trashed_at: now } })));
}

/** Evernote: a note whose notebook was deleted meanwhile is restored into the default notebook. */
export async function restoreNotes(ids: string[]) {
  if (!ids.length) return;
  const orphans = await db.query<{ id: string }>(
    `SELECT n.id FROM notes n LEFT JOIN notebooks nb ON nb.id = n.notebook_id AND nb.deleted = 0 AND nb.kind = 'notebook'
     WHERE n.id IN (${ids.map(() => "?").join(",")}) AND nb.id IS NULL`,
    ids,
  );
  const lost = new Set(orphans.map((r) => r.id));
  const fallback = lost.size ? await defaultNotebookId() : null;
  return db.mutate(ids.map((id) => ({ t: "notes" as const, id, set: lost.has(id) ? { trashed_at: null, notebook_id: fallback } : { trashed_at: null } })));
}

/** Delete mutations for the shortcuts pointing at these notes, notebooks, stacks or tags. */
async function shortcutDeletes(targetIds: string[]) {
  if (!targetIds.length) return [];
  const rows = await db.query<{ id: string }>(`SELECT id FROM shortcuts WHERE deleted = 0 AND target_id IN (${targetIds.map(() => "?").join(",")})`, targetIds);
  return rows.map((r) => ({ t: "shortcuts" as const, id: r.id, del: true as const }));
}

export async function deleteNotesForever(ids: string[]) {
  const tasks = await db.query<{ id: string }>(`SELECT id FROM tasks WHERE deleted = 0 AND note_id IN (${ids.map(() => "?").join(",")})`, ids);
  const atts = await db.query<{ id: string }>(`SELECT id FROM attachments WHERE deleted = 0 AND note_id IN (${ids.map(() => "?").join(",")})`, ids);
  await db.mutate([
    ...(await shortcutDeletes(ids)),
    ...ids.map((id) => ({ t: "notes" as const, id, del: true as const })),
    ...tasks.map((r) => ({ t: "tasks" as const, id: r.id, del: true as const })),
    ...atts.map((r) => ({ t: "attachments" as const, id: r.id, del: true as const })),
  ]);
}

export async function emptyTrash() {
  const rows = await db.query<{ id: string }>("SELECT id FROM notes WHERE deleted = 0 AND trashed_at IS NOT NULL");
  if (rows.length) await deleteNotesForever(rows.map((r) => r.id));
}

/**
 * Duplicate / "Copy to": the copy gets its own task and attachment rows (same files,
 * content-addressed), so deleting either note never breaks the other.
 */
export async function duplicateNote(id: string, notebookId?: string): Promise<string | undefined> {
  const n = await db.get<Note>("SELECT * FROM notes WHERE id = ?", [id]);
  if (!n) return;
  const copyId = newId();
  const now = Date.now();
  let html = n.body_html;
  const tasks = await db.query<Task>("SELECT * FROM tasks WHERE note_id = ? AND deleted = 0", [id]);
  const atts = await db.query<Attachment>("SELECT * FROM attachments WHERE note_id = ? AND deleted = 0", [id]);
  const muts: LocalMutation[] = [];
  for (const t of tasks) {
    const tid = newId();
    html = html.replaceAll(`data-task-id="${t.id}"`, `data-task-id="${tid}"`);
    muts.push({
      t: "tasks",
      id: tid,
      set: { note_id: copyId, title: t.title, completed_at: t.completed_at, due_at: t.due_at, due_all_day: t.due_all_day, reminder_at: t.reminder_at, flagged: t.flagged, recurrence: t.recurrence, position: t.position, created: now, updated: now },
    });
  }
  for (const a of atts) {
    const aid = newId();
    html = html.replaceAll(`data-attachment="${a.id}"`, `data-attachment="${aid}"`);
    muts.push({ t: "attachments", id: aid, set: { note_id: copyId, sha256: a.sha256, filename: a.filename, mime: a.mime, size: a.size, width: a.width, height: a.height, created: now } });
  }
  await db.mutate([
    {
      t: "notes",
      id: copyId,
      set: { notebook_id: notebookId ?? n.notebook_id, title: n.title, body_html: html, body_text: n.body_text, tag_ids: n.tag_ids, created: now, updated: now, trashed_at: null, source_url: n.source_url, pinned_at: null },
    },
    ...muts,
  ]);
  return copyId;
}

/**
 * Evernote's "Merge": the other notes' contents go under the first note's, each after a divider and its
 * title; tags are combined; the others move to Trash. Their tasks and files are copied (new rows), so a
 * note restored from the Trash is still whole.
 */
export async function mergeNotes(ids: string[]): Promise<string | undefined> {
  const [targetId, ...rest] = ids;
  if (!targetId || !rest.length) return targetId;
  const notes = await db.query<Note>(`SELECT * FROM notes WHERE id IN (${ids.map(() => "?").join(",")}) AND deleted = 0`, ids);
  const byId = new Map(notes.map((n) => [n.id, n]));
  const target = byId.get(targetId);
  if (!target) return;
  const now = Date.now();
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = target.body_html;
  let text = target.body_text;
  const tags = new Set(parseTagIds(target.tag_ids));
  const muts: LocalMutation[] = [];
  for (const id of rest) {
    const n = byId.get(id);
    if (!n) continue;
    let body = n.body_html;
    for (const t of await db.query<Task>("SELECT * FROM tasks WHERE note_id = ? AND deleted = 0", [id])) {
      const tid = newId();
      body = body.replaceAll(`data-task-id="${t.id}"`, `data-task-id="${tid}"`);
      muts.push({
        t: "tasks",
        id: tid,
        set: { note_id: targetId, title: t.title, completed_at: t.completed_at, due_at: t.due_at, due_all_day: t.due_all_day, reminder_at: t.reminder_at, flagged: t.flagged, recurrence: t.recurrence, position: t.position, created: now, updated: now },
      });
    }
    for (const a of await db.query<Attachment>("SELECT * FROM attachments WHERE note_id = ? AND deleted = 0", [id])) {
      const aid = newId();
      body = body.replaceAll(`data-attachment="${a.id}"`, `data-attachment="${aid}"`);
      muts.push({ t: "attachments", id: aid, set: { note_id: targetId, sha256: a.sha256, filename: a.filename, mime: a.mime, size: a.size, width: a.width, height: a.height, created: now } });
    }
    html += `<hr><h2>${esc(n.title || "Untitled")}</h2>${body}`;
    text += `\n${n.title}\n${n.body_text}`;
    for (const t of parseTagIds(n.tag_ids)) tags.add(t);
    muts.push({ t: "notes", id, set: { trashed_at: now } });
  }
  await db.mutate([{ t: "notes", id: targetId, set: { body_html: html, body_text: text.normalize("NFC"), tag_ids: JSON.stringify([...tags]), updated: now } }, ...muts]);
  return targetId;
}

export function moveNotes(ids: string[], notebookId: string) {
  const now = Date.now();
  return db.mutate(ids.map((id) => ({ t: "notes" as const, id, set: { notebook_id: notebookId, updated: now } })));
}

export async function addTagToNotes(ids: string[], tagId: string) {
  const rows = await db.query<{ id: string; tag_ids: string }>(`SELECT id, tag_ids FROM notes WHERE id IN (${ids.map(() => "?").join(",")})`, ids);
  const muts = rows
    .filter((r) => !parseTagIds(r.tag_ids).includes(tagId))
    .map((r) => ({ t: "notes" as const, id: r.id, set: { tag_ids: JSON.stringify([...parseTagIds(r.tag_ids), tagId]) } }));
  if (muts.length) await db.mutate(muts);
}

export async function removeTagFromNotes(ids: string[], tagId: string) {
  const rows = await db.query<{ id: string; tag_ids: string }>(`SELECT id, tag_ids FROM notes WHERE id IN (${ids.map(() => "?").join(",")})`, ids);
  const muts = rows
    .filter((r) => parseTagIds(r.tag_ids).includes(tagId))
    .map((r) => ({ t: "notes" as const, id: r.id, set: { tag_ids: JSON.stringify(parseTagIds(r.tag_ids).filter((x) => x !== tagId)) } }));
  if (muts.length) await db.mutate(muts);
}

export async function createNotebook(name: string, stackId: string | null = null): Promise<string> {
  const id = newId();
  const now = Date.now();
  await db.mutate([{ t: "notebooks", id, set: { name, kind: "notebook", parent_id: stackId, created: now, updated: now } }]);
  return id;
}

export async function createStack(name: string): Promise<string> {
  const id = newId();
  const now = Date.now();
  await db.mutate([{ t: "notebooks", id, set: { name, kind: "stack", parent_id: null, created: now, updated: now } }]);
  return id;
}

export function renameNotebook(id: string, name: string) {
  return db.mutate([{ t: "notebooks", id, set: { name, updated: Date.now() } }]);
}

export function moveNotebookToStack(id: string, stackId: string | null) {
  return db.mutate([{ t: "notebooks", id, set: { parent_id: stackId, updated: Date.now() } }]);
}

/** Evernote: deleting a notebook moves its notes to the trash. */
export async function deleteNotebook(id: string) {
  const nb = await db.get<Notebook>("SELECT * FROM notebooks WHERE id = ?", [id]);
  if (!nb) return;
  if (nb.kind === "stack") {
    const children = await db.query<{ id: string }>("SELECT id FROM notebooks WHERE parent_id = ? AND deleted = 0", [id]);
    await db.mutate([...(await shortcutDeletes([id])), ...children.map((c) => ({ t: "notebooks" as const, id: c.id, set: { parent_id: nb.parent_id ?? null } })), { t: "notebooks", id, del: true }]);
    return;
  }
  const notes = await db.query<{ id: string }>("SELECT id FROM notes WHERE notebook_id = ? AND deleted = 0 AND trashed_at IS NULL", [id]);
  const now = Date.now();
  await db.mutate([...(await shortcutDeletes([id])), ...notes.map((n) => ({ t: "notes" as const, id: n.id, set: { trashed_at: now } })), { t: "notebooks", id, del: true }]);
}

/** Evernote names are unique (ignoring case): notebooks among notebooks, stacks among stacks, tags among tags. */
export async function nameTaken(table: "notebooks" | "tags", name: string, opts: { kind?: "notebook" | "stack"; exceptId?: string } = {}): Promise<boolean> {
  const kind = table === "notebooks" ? " AND kind = ?" : "";
  const params: unknown[] = [name.trim(), opts.exceptId ?? "", ...(table === "notebooks" ? [opts.kind ?? "notebook"] : [])];
  return !!(await db.get(`SELECT 1 FROM ${table} WHERE deleted = 0 AND name = ? COLLATE NOCASE AND id != ?${kind}`, params));
}

/** Validators for name prompts. */
export const notebookNameError = (kind: "notebook" | "stack", exceptId?: string) => async (v: string) =>
  (await nameTaken("notebooks", v, { kind, exceptId })) ? `A ${kind} named "${v}" already exists.` : null;
export const tagNameError = (exceptId?: string) => async (v: string) =>
  (await nameTaken("tags", v, { exceptId })) ? `A tag named "${v}" already exists.` : null;

// Creating a tag checks for an existing one first; serialize so quick repeats (Enter, Enter) can't race into duplicates.
let tagQueue: Promise<unknown> = Promise.resolve();
export function createTag(name: string, parentId: string | null = null): Promise<string> {
  const run = async () => {
    const existing = await db.get<Tag>("SELECT * FROM tags WHERE deleted = 0 AND name = ? COLLATE NOCASE", [name.trim()]);
    if (existing) return existing.id;
    const id = newId();
    const now = Date.now();
    await db.mutate([{ t: "tags", id, set: { name: name.trim(), parent_id: parentId, created: now, updated: now } }]);
    return id;
  };
  const p = tagQueue.then(run, run);
  tagQueue = p.catch(() => undefined);
  return p;
}

export function renameTag(id: string, name: string) {
  return db.mutate([{ t: "tags", id, set: { name, updated: Date.now() } }]);
}

export async function deleteTag(id: string) {
  const notes = await db.query<{ id: string; tag_ids: string }>(
    "SELECT n.id, n.tag_ids FROM notes n, json_each(n.tag_ids) je WHERE je.value = ? AND n.deleted = 0",
    [id],
  );
  const children = await db.query<{ id: string }>("SELECT id FROM tags WHERE parent_id = ? AND deleted = 0", [id]);
  await db.mutate([
    ...(await shortcutDeletes([id])),
    ...notes.map((n) => ({ t: "notes" as const, id: n.id, set: { tag_ids: JSON.stringify(parseTagIds(n.tag_ids).filter((x) => x !== id)) } })),
    ...children.map((c) => ({ t: "tags" as const, id: c.id, set: { parent_id: null } })),
    { t: "tags", id, del: true },
  ]);
}

export async function setNoteTags(noteId: string, tagIds: string[]) {
  await db.mutate([{ t: "notes", id: noteId, set: { tag_ids: JSON.stringify([...new Set(tagIds)]) } }]);
}

export async function addShortcut(kind: "note" | "notebook" | "stack" | "tag" | "search", targetId: string) {
  const exists = await db.get("SELECT 1 FROM shortcuts WHERE kind = ? AND target_id = ? AND deleted = 0", [kind, targetId]);
  if (exists) return;
  const max = await db.get<{ p: number | null }>("SELECT MAX(position) AS p FROM shortcuts WHERE deleted = 0");
  await db.mutate([{ t: "shortcuts", id: newId(), set: { kind, target_id: targetId, position: (max?.p ?? 0) + 1, created: Date.now() } }]);
}

export async function removeShortcut(kind: string, targetId: string) {
  const rows = await db.query<{ id: string }>("SELECT id FROM shortcuts WHERE kind = ? AND target_id = ? AND deleted = 0", [kind, targetId]);
  await db.mutate(rows.map((r) => ({ t: "shortcuts" as const, id: r.id, del: true as const })));
}

/** Shortcuts in their new order (Evernote: drag to reorder). Only rows whose position changed are written. */
export async function reorderShortcuts(ids: string[]) {
  const rows = await db.query<{ id: string; position: number }>("SELECT id, position FROM shortcuts WHERE deleted = 0");
  const pos = new Map(rows.map((r) => [r.id, r.position]));
  const muts = ids.flatMap((id, i) => (pos.get(id) === i + 1 ? [] : [{ t: "shortcuts" as const, id, set: { position: i + 1 } }]));
  if (muts.length) await db.mutate(muts);
}

export async function saveSearch(name: string, query: string): Promise<string> {
  const id = newId();
  const now = Date.now();
  await db.mutate([{ t: "saved_searches", id, set: { name, query, created: now, updated: now } }]);
  return id;
}

export function updateSavedSearch(id: string, set: { name?: string; query?: string }) {
  return db.mutate([{ t: "saved_searches", id, set: { ...set, updated: Date.now() } }]);
}

export async function deleteSavedSearch(id: string) {
  const sc = await db.query<{ id: string }>("SELECT id FROM shortcuts WHERE kind = 'search' AND target_id = ? AND deleted = 0", [id]);
  await db.mutate([...sc.map((r) => ({ t: "shortcuts" as const, id: r.id, del: true as const })), { t: "saved_searches", id, del: true }]);
}

/* ---------- tasks ---------- */

export async function toggleTask(id: string) {
  const t = await db.get<Task>("SELECT * FROM tasks WHERE id = ?", [id]);
  if (t) await db.mutate([{ t: "tasks", id, set: toggleCompletion(t) }]);
}

export function updateTask(id: string, set: Partial<Pick<Task, "title" | "due_at" | "due_all_day" | "reminder_at" | "flagged" | "recurrence" | "completed_at">>) {
  return db.mutate([{ t: "tasks", id, set: { ...set, updated: Date.now() } }]);
}

/** Evernote's "default task note": loose tasks created outside a note go here. */
export async function defaultTaskNoteId(): Promise<string> {
  const id = await getSetting<string>("defaultTaskNote");
  if (id && (await db.get("SELECT 1 FROM notes WHERE id = ? AND deleted = 0 AND trashed_at IS NULL", [id]))) return id;
  const note = await createNote({ title: "Tasks" });
  await setSetting("defaultTaskNote", note);
  return note;
}

export async function createTask(
  opts: { id?: string; title?: string; noteId?: string; due_at?: number | null; due_all_day?: number; reminder_at?: number | null; recurrence?: string | null; flagged?: boolean } = {},
): Promise<string> {
  const noteId = opts.noteId ?? (await defaultTaskNoteId());
  const id = opts.id ?? newId();
  const now = Date.now();
  const note = await db.get<Note>("SELECT body_html FROM notes WHERE id = ?", [noteId]);
  await db.mutate([
    {
      t: "tasks",
      id,
      set: {
        note_id: noteId,
        title: opts.title ?? "",
        completed_at: null,
        due_at: opts.due_at ?? null,
        due_all_day: opts.due_all_day ?? (opts.due_at ? 1 : 0),
        reminder_at: opts.reminder_at ?? null,
        flagged: opts.flagged ? 1 : 0,
        recurrence: opts.recurrence ?? null,
        position: now,
        created: now,
        updated: now,
      },
    },
    { t: "notes", id: noteId, set: { body_html: (note?.body_html ?? "") + `<div data-task-id="${id}"></div>`, updated: now } },
  ]);
  return id;
}

/**
 * Moves a task to another note: its placeholder leaves the old note's body and is appended to the new one. An open
 * editor showing the old note drops the node itself (engram:task-moved), so its next save doesn't bring it back.
 */
export async function moveTask(id: string, noteId: string) {
  const t = await db.get<Task>("SELECT note_id FROM tasks WHERE id = ?", [id]);
  if (!t || t.note_id === noteId) return;
  const [from, to] = await Promise.all([
    db.get<Note>("SELECT body_html FROM notes WHERE id = ?", [t.note_id]),
    db.get<Note>("SELECT body_html FROM notes WHERE id = ?", [noteId]),
  ]);
  const now = Date.now();
  const placeholder = `<div data-task-id="${id}"></div>`;
  window.dispatchEvent(new CustomEvent("engram:task-moved", { detail: { id, from: t.note_id } }));
  await db.mutate([
    { t: "tasks", id, set: { note_id: noteId, updated: now } },
    ...(from ? [{ t: "notes" as const, id: t.note_id, set: { body_html: from.body_html.replace(placeholder, ""), updated: now } }] : []),
    { t: "notes", id: noteId, set: { body_html: (to?.body_html ?? "") + placeholder, updated: now } },
  ]);
}

/** `keepBody`: the caller (the open editor) already removed the placeholder from the note. */
export async function deleteTask(id: string, opts: { keepBody?: boolean } = {}) {
  if (opts.keepBody) return db.mutate([{ t: "tasks", id, del: true }]);
  const t = await db.get<Task>("SELECT note_id FROM tasks WHERE id = ?", [id]);
  const note = t ? await db.get<Note>("SELECT body_html FROM notes WHERE id = ?", [t.note_id]) : undefined;
  await db.mutate([
    { t: "tasks", id, del: true },
    ...(t && note ? [{ t: "notes" as const, id: t.note_id, set: { body_html: note.body_html.replace(`<div data-task-id="${id}"></div>`, ""), updated: Date.now() } }] : []),
  ]);
}
